import { Injectable } from "@angular/core";
import { StoreApi } from "../api/store.api";
import { NotificationsService } from "angular2-notifications";
import { IQueryFilter, QueryResult } from "../model/query.filter.class";
import { Observable } from "rxjs/internal/Observable";
import { apiCallWrapper } from "../api/api.util";
import { LoadingService } from "./core/loading.service";
import { IStore, IStoreOverview, IStorePickup, IStoreProductResponse } from "../model/store.model";
import { HasId } from "../model/generics";
import { BehaviorSubject, combineLatest, filter, first } from "rxjs";
import { SubscriptionGroup } from "../util/subscriptionGroup";
import { SecurityService } from "./security.service";
import { SessionApi } from "../api/session.api";
import { ICustomerUser } from "../model/customer.user.model";

@Injectable({
  providedIn: "root",
})
export class StoreService {
  public readonly allStores$ = new BehaviorSubject(
    new QueryResult<IStore & HasId<string>>(),
  );
  private readonly subscriptionGroup = new SubscriptionGroup();

  constructor(
    private storeApi: StoreApi,
    private notifications: NotificationsService,
    private loadingService: LoadingService,
    private security: SecurityService,
    private sessionApi: SessionApi,
  ) {
    this.monitorStores();
  }

  monitorStores() {
    this.subscriptionGroup.add(
      this.security.isAuthenticated().subscribe({
        next: () => {
          this.refreshCategoryCache();
        },
      }),
    );
  }

  public refreshCategoryCache() {
    const authState = this.sessionApi.$userData.getValue();
    if (authState && !authState.isAdmin) {
      this.storeApi
        .list(
          new IQueryFilter({
            filter: { showAll: true, storeProduct: true },
          }),
        )
        .pipe(first())
        .subscribe({
          next: (val) => {
            this.allStores$.next(
              val instanceof QueryResult ? val : new QueryResult(val),
            );
          },
        });
    } else {
      this.allStores$.next(new QueryResult<IStore & HasId<string>>());
    }
  }

  public list(
    query: IQueryFilter,
    showNotification = false,
  ): Observable<QueryResult<IStore>> {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(this.storeApi.list(query)),
      showNotification
        ? {
          notificationsService: this.notifications,
          action: "Fetching store",
        }
        : undefined,
    );
  }

  public create(data: IStore) {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(this.storeApi.create(data)),
      {
        notificationsService: this.notifications,
        action: "Create Store",
      },
    );
  }

  public delete(id: number): Observable<{ success: boolean; deleted: number }> {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(this.storeApi.delete(id)),
      {
        notificationsService: this.notifications,
        action: "Delete Store",
      },
    );
  }

  public get(id: number): Observable<Partial<IStore>> {
    return apiCallWrapper(this.storeApi.get(id), {
      notificationsService: this.notifications,
      action: "Fetching Store By Id",
    });
  }

  public update(id: number, data: IStore) {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.storeApi.update(id, data),
      ),
      {
        notificationsService: this.notifications,
        action: "Update Store",
      },
    );
  }

  public productlist(
    query: IQueryFilter,
    showNotification = true,
  ): Observable<QueryResult<IStoreProductResponse>> {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.storeApi.productList(query),
      ),
      showNotification
        ? {
          notificationsService: this.notifications,
          action: "Fetching Store Products",
        }
        : undefined,
    );
  }

  public employeelist(
    query: IQueryFilter,
    showNotification = true,
  ): Observable<QueryResult<ICustomerUser>> {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.storeApi.employeeList(query),
      ),
      showNotification
        ? {
          notificationsService: this.notifications,
          action: "Fetching Store Employees",
        }
        : undefined,
    );
  }

  public checkStoreExist(data: { id?: string, name: string }): Observable<{ isExist: boolean }> {
    return this.loadingService.blockWithLoadingOverlayRx(this.storeApi.checkStoreExist(data));
  }

  public overviewSummary(
    storeId: string | null,
    showNotification = false,
  ): Observable<IStoreOverview> {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.storeApi.overviewSummary(storeId),
      ),
      showNotification
        ? {
          notificationsService: this.notifications,
          action: "Fetching Store Overview Summary",
        }
        : undefined,
    );
  }

  public restore(id: number | string) {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(this.storeApi.restore(id)),
      {
        notificationsService: this.notifications,
        action: "Restore Onsite-Store",
      }
    );
  }

}
